import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { ScanLine, Calculator, Settings, FileSpreadsheet, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL

const translations = {
  el: {
    hero: {
      title: 'Λογισμικό Εκτίμησης Κόστους Κατασκευής',
      subtitle: 'Πρόγραμμα κοστολόγησης για μεταλλουργεία και εργαστήρια CNC: εισάγετε DXF ή STEP, πάρτε το κόστος κάθε εξαρτήματος και στείλτε προσφορά σε λίγα λεπτά.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Δείτε Τιμές',
    },
    intro: {
      title: 'Τι κάνει ένα λογισμικό εκτίμησης κόστους κατασκευής',
      body: 'Ένα λογισμικό εκτίμησης κόστους κατασκευής υπολογίζει τι θα κοστίσει η παραγωγή ενός εξαρτήματος πριν δώσετε τιμή: υλικό, χρόνο μηχανής, εργατικά, ενέργεια και περιθώριο κέρδους. Το Workshop Assistant το κάνει απευθείας από τα αρχεία CAD για εξαρτήματα λαμαρίνας (laser, ψαλίδι, στράντζα), σωλήνες laser και εξαρτήματα φρέζας CNC router, με τις δικές σας τιμές.',
    },
    steps: {
      title: 'Από το σχέδιο στην προσφορά σε τρία βήματα',
      items: [
        { title: 'Εισαγωγή αρχείων', description: 'DXF με πάχος που ορίζετε, STEP με αυτόματη αναγνώριση πάχους και στραντζαρισμάτων, ή απευθείας εξαγωγή από SolidWorks και Inventor.' },
        { title: 'Αυτόματη κοστολόγηση', description: 'Το πρόγραμμα μετρά περιμέτρους, οπές και στραντζαρίσματα και υπολογίζει υλικό, κοπή, κάμψη, εργατικά και ρεύμα για κάθε εξάρτημα.' },
        { title: 'Προσφορά και αναφορά', description: 'Ορίστε ποσότητες και κέρδος και βγάλτε προσφορά Excel με ανάλυση κόστους ανά τεμάχιο.' },
      ],
    },
    features: {
      title: 'Γιατί το επιλέγουν τα εργαστήρια',
      items: [
        { icon: 'scan', title: 'Κόστος από τη Γεωμετρία', description: 'Οι υπολογισμοί βασίζονται στις πραγματικές διαστάσεις του εξαρτήματος, όχι σε εκτιμήσεις με το μάτι.' },
        { icon: 'calc', title: 'Όλες οι Διεργασίες σε Ένα Πρόγραμμα', description: 'Laser, ψαλίδι, στράντζα, laser σωλήνα και φρέζα CNC κοστολογούνται με την ίδια λογική.' },
        { icon: 'settings', title: 'Οι Δικές σας Τιμές', description: 'Τιμές υλικών, μηχανών, εργατοώρας, ρεύματος και κέρδους ρυθμίζονται ώστε να ταιριάζουν στο εργαστήριό σας.' },
        { icon: 'excel', title: 'Αναλυτικές Αναφορές', description: 'Κάθε προσφορά έχει πλήρη ανάλυση κόστους, ώστε να ξέρετε πού βγαίνει το κέρδος.' },
      ],
    },
    faq: [
      { q: 'Τι είναι ένας εκτιμητής κόστους κατασκευής;', a: 'Ένα πρόγραμμα που υπολογίζει το κόστος παραγωγής ενός εξαρτήματος (υλικό, χρόνο μηχανής, εργατικά, ενέργεια και κέρδος) πριν αυτό κατασκευαστεί, ώστε η προσφορά να βασίζεται σε πραγματικά νούμερα.' },
      { q: 'Για ποιες διεργασίες υπολογίζει κόστος το Workshop Assistant;', a: 'Κοπή λαμαρίνας με laser ή ψαλίδι, κάμψη σε στράντζα, laser κοπή σωλήνων και προφίλ, και 2D φρεζάρισμα φύλλων με CNC router. Δεν κοστολογεί 3-αξονική κατεργασία συμπαγών μεταλλικών τεμαχίων ή τόρνευση.' },
      { q: 'Τι αρχεία χρειάζομαι;', a: 'Αρχεία DXF ή STEP, ή εξαγωγή από SolidWorks και Autodesk Inventor μέσω του δωρεάν πρόσθετου SheetMetalToJSON.' },
      { q: 'Υπάρχει δωρεάν δοκιμή;', a: 'Ναι. Η δοκιμαστική έκδοση κατεβαίνει δωρεάν, χωρίς πιστωτική κάρτα, για Windows και Android.' },
    ],
    related: 'Δείτε επίσης',
    cta: { title: 'Δοκιμάστε τον Εκτιμητή Κόστους Δωρεάν', subtitle: 'Κατεβάστε το Workshop Assistant και κοστολογήστε τα πρώτα σας εξαρτήματα σε λεπτά.', btn: 'Δωρεάν Δοκιμή' },
  },
  en: {
    hero: {
      title: 'Manufacturing Cost Estimator Software',
      subtitle: 'Costing software for fabrication and CNC shops: import DXF or STEP files, get the cost of every part, and send a quote in minutes.',
      cta1: 'Download Free Trial',
      cta2: 'View Pricing',
    },
    intro: {
      title: 'What manufacturing cost estimating software does',
      body: 'Manufacturing cost estimating software works out what a part will cost to produce before you price it: material, machine time, labor, energy and margin. Workshop Assistant does this straight from your CAD files for sheet metal parts (laser, guillotine, press brake), laser-cut tube and CNC router parts, using your own shop rates.',
    },
    steps: {
      title: 'From drawing to quote in three steps',
      items: [
        { title: 'Import your files', description: 'DXF with the thickness you set, STEP with automatic thickness and bend detection, or a direct export from SolidWorks and Inventor.' },
        { title: 'Automatic costing', description: 'The program measures perimeters, holes and bends, then calculates material, cutting, bending, labor and electricity for every part.' },
        { title: 'Quote and report', description: 'Set quantities and margin, then generate an Excel quote with a per-part cost breakdown.' },
      ],
    },
    features: {
      title: 'Why shops use it',
      items: [
        { icon: 'scan', title: 'Cost from Real Geometry', description: 'Calculations use the part\'s actual dimensions, not a rough guess from the drawing.' },
        { icon: 'calc', title: 'Every Process in One Program', description: 'Laser, guillotine, press brake, tube laser and CNC router are all costed with the same method.' },
        { icon: 'settings', title: 'Your Own Rates', description: 'Material prices, machine rates, labor, electricity and margin are set to match your workshop.' },
        { icon: 'excel', title: 'Detailed Reports', description: 'Every quote carries a full cost breakdown, so you know where the profit comes from.' },
      ],
    },
    faq: [
      { q: 'What is a manufacturing cost estimator?', a: 'A program that calculates the production cost of a part (material, machine time, labor, energy and margin) before it is made, so quotes are based on real numbers instead of guesswork.' },
      { q: 'Which processes does Workshop Assistant cost?', a: 'Sheet metal cutting by laser or guillotine, press brake bending, laser cutting of tubes and profiles, and 2D CNC router milling of sheet stock. It does not estimate 3-axis machining of solid metal blocks or turning.' },
      { q: 'What files do I need?', a: 'DXF or STEP files, or an export from SolidWorks and Autodesk Inventor through the free SheetMetalToJSON add-in.' },
      { q: 'Is there a free trial?', a: 'Yes. The trial version is a free download, no credit card required, for Windows and Android.' },
    ],
    related: 'Related',
    cta: { title: 'Try the Cost Estimator Free', subtitle: 'Download Workshop Assistant and cost your first parts in minutes.', btn: 'Download Free Trial' },
  },
}

const related = {
  el: [
    { name: 'Κοστολόγηση Μεταλλικών Εξαρτημάτων', path: '/metal-parts-costing' },
    { name: 'Κοστολόγηση Φρέζας CNC', path: '/cnc-milling-costing' },
    { name: 'Δωρεάν Υπολογιστής Κόστους', path: '/cost-calculator' },
    { name: 'Οδηγός Κοστολόγησης Λαμαρίνας', path: '/sheet-metal-costing' },
  ],
  en: [
    { name: 'Metal Parts Costing', path: '/metal-parts-costing' },
    { name: 'CNC Milling Costing', path: '/cnc-milling-costing' },
    { name: 'Free Cost Calculator', path: '/cost-calculator' },
    { name: 'Sheet Metal Costing Guide', path: '/sheet-metal-costing' },
  ],
}

const icons = { scan: <ScanLine className="h-10 w-10" />, calc: <Calculator className="h-10 w-10" />, settings: <Settings className="h-10 w-10" />, excel: <FileSpreadsheet className="h-10 w-10" /> }

const ManufacturingCostEstimator = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Manufacturing Cost Estimator & Costing Software"
        description="Manufacturing cost estimator software for metal fabrication and CNC shops. Cost sheet metal, tube and router parts from DXF/STEP files and send quotes in minutes. Free trial."
        path="/manufacturing-cost-estimator"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/pricing" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
            <p className="text-lg text-gray-600">{t.intro.body}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img src={`${BASE}screenshots/calculator.png?v=2`} alt={t.hero.title} className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">{t.steps.title}</h2>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.steps.items.map((s, i) => (
            <li key={i} className="card">
              <div className="text-4xl font-bold text-primary-600 mb-3">{i + 1}</div>
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-gray-600">{s.description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section-container bg-white">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
          <h2 className="text-2xl font-bold mt-12 mb-4">{t.related}</h2>
          <ul className="space-y-2">
            {related[lang].map((r) => (
              <li key={r.path}>
                <Link to={r.path} className="inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-700">
                  {r.name}<ArrowRight className="h-4 w-4" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ManufacturingCostEstimator
